import React from 'react';
import { GrServices } from "react-icons/gr";
import { FaNetworkWired, FaCheck, FaMapMarkerAlt, FaShieldAlt, FaTruck, FaDatabase } from 'react-icons/fa';
import NavBar from '../../../component/b2c/navBar/navBar';
import Sidebar from '../../../component/b2c/sidebar/sidebar';
import { useMyContext } from '../../../context/MyContext'
import '../../../css/custom-scrollbar.css'

const LogisticsServicePage = () => {
  const { isDarkMode, isToggleSidebar } = useMyContext();

  const services = [
    {
      icon: FaNetworkWired,
      title: 'API Integration',
      description: 'Seamless connectivity with your existing systems through robust and flexible API solutions.',
      accent: 'from-gray-700 to-gray-500',
      glow: 'group-hover:shadow-gray-500/30',
      tag: 'Connectivity'
    },
    {
      icon: FaCheck,
      title: 'Order Confirmation',
      description: 'Real-time order tracking and instant confirmation to provide transparency and peace of mind.',
      accent: 'from-gray-700 to-gray-500',
      glow: 'group-hover:shadow-gray-500/30',
      tag: 'Real-time'
    },
    {
      icon: FaMapMarkerAlt,
      title: 'Fraud Detection',
      description: 'Advanced algorithmic protection to identify and prevent potential fraudulent activities.',
      accent: 'from-gray-700 to-gray-500',
      glow: 'group-hover:shadow-gray-500/30',
      tag: 'Protection'
    },
    {
      icon: FaShieldAlt,
      title: 'Security Protocols',
      description: 'Comprehensive security measures to protect your data and ensure compliance.',
      accent: 'from-gray-700 to-gray-500',
      glow: 'group-hover:shadow-gray-500/30',
      tag: 'Compliance'
    },
    {
      icon: FaTruck,
      title: 'Logistics Tracking',
      description: 'End-to-end tracking of shipments with real-time updates and precise location monitoring.',
      accent: 'from-gray-700 to-gray-500',
      glow: 'group-hover:shadow-gray-500/30',
      tag: 'Visibility'
    },
    {
      icon: FaDatabase,
      title: 'Warehousing and fulfillment',
      description: "Efficient storage, inventory management, and order fulfillment solutions to streamline logistics and enhance supply chain efficiency.",
      accent: 'from-gray-700 to-gray-500',
      glow: 'group-hover:shadow-gray-500/30',
      tag: 'Fulfillment'
    }
  ];
  const glass =
        "backdrop-blur-2xl backdrop-saturate-150 bg-white/50 dark:bg-white/[0.06] " +
        "border border-white/60 dark:border-white/10 " +
        "shadow-[0_8px_32px_rgba(31,41,55,0.08)]";
  return (
    <div className={`h-screen flex flex-col overflow-hidden ${isDarkMode ? 'dark:bg-darkmodeColor' : 'bg-white'} transition-colors duration-300`}>
         <div className="pointer-events-none absolute -top-32 -left-24 h-96 w-96 rounded-full bg-indigo-200/30 dark:bg-indigo-500/10 blur-3xl" />
     
                 <NavBar />
                 <div className="flex flex-1 overflow-hidden">
                     {isToggleSidebar && (
                         <div className={`w-64 m-3 rounded-3xl ${glass} overflow-hidden`}>
                             <Sidebar />
                         </div>
                     )}
        <div className="flex-1 p-3 bg-gray-200 dark:bg-darkmodeColor overflow-hidden">

         

          <div className="relative max-w-7xl mx-auto">

            {/* Hero */}
            <div className="text-center pt-2">
              <div className="flex justify-center">
                <div className="relative">
                  <div className="absolute -inset-3 bg-gradient-to-tr from-gray-600 to-gray-400 rounded-full blur-2xl opacity-30 animate-pulse"></div>
                  <div className="relative bg-white dark:bg-darkmodeColor p-3 rounded-full shadow-2xl ring-1 ring-black/5 dark:ring-white/10">
                    <GrServices className="w-8 h-8 text-gray-700 dark:text-gray-300" />
                  </div>
                </div>
              </div>

              <span className="inline-block mb-2 text-[10px] font-semibold tracking-[0.2em] uppercase text-gray-500 dark:text-gray-400">
                Pickupxpress
              </span>

              <h1 className="text-lg sm:text-xl md:text-2xl font-black tracking-tight text-gray-900 dark:text-white leading-tight">
                Logistics Services, Built for Motion
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
                Comprehensive, connected solutions that keep every shipment visible, secure, and on time — from checkout to doorstep.
              </p>

              {/* Signature: animated route line */}
              <div className="relative max-w-md mx-auto h-6">
                <svg viewBox="0 0 400 32" className="w-full h-full">
                  <line x1="10" y1="16" x2="390" y2="16" stroke="currentColor" strokeWidth="2" strokeDasharray="2 8" strokeLinecap="round" className="text-gray-300 dark:text-gray-600" />
                  <circle cx="10" cy="16" r="4" className="fill-gray-700" />
                  <circle cx="390" cy="16" r="4" className="fill-gray-500" />
                  <circle cy="16" r="3" className="fill-gray-600">
                    <animate attributeName="cx" values="10;390;10" dur="4s" repeatCount="indefinite" />
                  </circle>
                </svg>
              </div>
            </div>

            {/* Bento grid of services */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {services.map((service, index) => {
                const Icon = service.icon;
                return (
                  <div
                    key={index}
                    className={`group relative rounded-xl p-[1px] bg-gradient-to-br ${service.accent} opacity-90 hover:opacity-100 transition-all duration-300 animate-fade-in-up`}
                    style={{ animationDelay: `${index * 90}ms` }}
                  >
                    <div className={`relative h-full rounded-xl bg-white/90 dark:bg-darkmodeColor backdrop-blur-xl p-4 transition-all duration-300 group-hover:-translate-y-0.5 shadow-md ${service.glow} group-hover:shadow-lg`}>
                      <div className="flex items-start justify-between mb-3">
                        <div className={`relative inline-flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br ${service.accent} shadow-md`}>
                          <Icon className="w-4 h-4 text-white" />
                        </div>
                        <span className="text-[9px] font-semibold tracking-wide uppercase text-gray-400 dark:text-gray-500 mt-1">
                          {service.tag}
                        </span>
                      </div>

                      <h2 className="text-base font-bold text-gray-800 dark:text-white mb-1.5">
                        {service.title}
                      </h2>
                      <p className="text-xs leading-relaxed text-gray-600 dark:text-gray-300">
                        {service.description}
                      </p>

                      <div className={`absolute bottom-0 left-4 right-4 h-px bg-gradient-to-r ${service.accent} scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left`}></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogisticsServicePage;